import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  TouchableOpacity,
  Image,
  ActivityIndicator
} from "react-native";
import Glob from "src/globalConstants";
import NavBar from "src/components/navBar";
import { MaterialIcons } from "@expo/vector-icons";
import moment from "moment";
import Database from "src/backend/database";
import Analytics from "src/backend/analytics";
import Util from "src/utility";
import Rex from "src/globalState";
import { Skeleton } from "native-base";
import ChildCard from "src/components/ChildCard";
import Icon from "src/components/Icon";

const { width } = Glob.get("dimensions");

export default function PaymentsCustomerChargeDetails({ route, navigation }) {
  const { charge } = route.params;
  const [children, setChildren] = useState({});
  const [classrooms, setClassrooms] = useState({});
  const [isLoadingChild, setIsLoadingChild] = useState(false);
  const [checkoutSession, setCheckoutSession] = useState(null);
  const [isLoadingCheckoutSession, setIsLoadingCheckoutSession] = useState(
    false
  );

  // Check if charge has a child attached
  const tcChildID = charge?.metadata?.tcChildID;

  useEffect(() => {
    Analytics.logEvent("view_paymentsCustomerChargeDetails");
    fetchCheckoutSession();
  }, []);

  useEffect(() => {
    if (tcChildID && Rex.getConfig()?.transparentClassroomIntegrationEnabled) {
      fetchChildData();
    }
  }, [tcChildID]);

  const fetchChildData = async () => {
    try {
      setIsLoadingChild(true);
      const response = await Database.tcCheckIntegrationStatus();
      const { status } = response || {};
      const connected = status === Glob.get("tcIntegrationStatuses").CONNECTED;

      if (connected) {
        const childrenResponse = await Database.tcFetchChildren();
        const childrenByID = {};
        (childrenResponse?.data || []).forEach((child) => {
          childrenByID[child.id] = child;
        });
        setChildren(childrenByID);

        // Fetch classrooms if needed
        if (childrenByID[tcChildID]?.classroom_ids?.length > 0) {
          const classroomsResponse = await Database.tcFetchClassrooms();
          const classroomsByID = {};
          (classroomsResponse?.data || []).forEach((classroom) => {
            classroomsByID[classroom.id] = classroom;
          });
          setClassrooms(classroomsByID);
        }
      }
    } catch (error) {
      console.error("Error fetching child data:", error);
    } finally {
      setIsLoadingChild(false);
    }
  };

  const fetchCheckoutSession = async () => {
    // Look for checkout session ID in metadata
    const checkoutSessionID = charge.metadata?.checkoutSessionID;
    if (!checkoutSessionID) return;

    setIsLoadingCheckoutSession(true);
    try {
      const sessionData = await Database.stripeConnectedAccountFetchCheckoutSession(
        {
          checkoutSessionID
        }
      );

      if (sessionData?.success) {
        setCheckoutSession(sessionData.session);
      }
    } catch (error) {
      console.error("Error fetching checkout session:", error);
    } finally {
      setIsLoadingCheckoutSession(false);
    }
  };

  // Get product information if available
  const productName =
    checkoutSession?.line_items?.data?.[0]?.price?.product?.name ||
    charge?.metadata?.productName ||
    charge.description ||
    "One-time Purchase";

  // Get the child data
  const child = tcChildID ? children[tcChildID] : null;

  // Format payment method display
  const getPaymentMethodDisplay = () => {
    if (charge.payment_method_details?.type === "card") {
      return `${charge.payment_method_details.card.brand
        .charAt(0)
        .toUpperCase() +
        charge.payment_method_details.card.brand.slice(1)} ending in ${
        charge.payment_method_details.card.last4
      }`;
    }
    if (charge.payment_method_details?.type === "us_bank_account") {
      return `Bank account ending in ${charge.payment_method_details.us_bank_account.last4}`;
    }
    return "Other";
  };

  // Render purchase details section
  const renderPurchaseDetailsSection = () => {
    if (isLoadingCheckoutSession) {
      return (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <MaterialIcons name="shopping-bag" size={20} color="#4A5568" />
            <Text style={styles.sectionTitle}>Purchase Details</Text>
          </View>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color="#4299E1" />
            <Text style={styles.loadingText}>Loading purchase details...</Text>
          </View>
        </View>
      );
    }

    // Check if we have a checkout session with line items
    const { line_items } = checkoutSession || {};
    const hasLineItems =
      line_items && line_items.data && line_items.data.length > 0;

    if (!hasLineItems) return null;

    // Check for customer question responses
    let questionResponses = null;
    try {
      if (checkoutSession.metadata?.questionResponses) {
        questionResponses = JSON.parse(
          checkoutSession.metadata.questionResponses
        );
      }
    } catch (e) {
      console.error("Error parsing question responses:", e);
    }

    // Check for shipping address
    const hasShipping = !!checkoutSession.shipping_details;

    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <MaterialIcons name="shopping-bag" size={20} color="#4A5568" />
          <Text style={styles.sectionTitle}>Purchase Details</Text>
        </View>

        {/* Line items */}
        {hasLineItems && (
          <View style={styles.lineItemsContainer}>
            {line_items.data.map((item, index) => {
              const product = item.price?.product;
              const quantity = item.quantity || 1;
              const unitAmount = item.price?.unit_amount / 100 || 0;
              const totalAmount = unitAmount * quantity;

              return (
                <View key={index} style={styles.lineItemContainer}>
                  {product?.images && product.images[0] && (
                    <Image
                      source={{ uri: product.images[0] }}
                      style={styles.productImage}
                      resizeMode="cover"
                    />
                  )}
                  <View style={styles.lineItemInfo}>
                    <Text style={styles.productName}>
                      {product?.name || "Product"}
                    </Text>
                    {product?.description && (
                      <Text style={styles.productDescription} numberOfLines={2}>
                        {product.description}
                      </Text>
                    )}
                    <View style={styles.lineItemDetails}>
                      <Text style={styles.productQuantity}>
                        Quantity: {quantity}
                      </Text>
                      <Text style={styles.productPrice}>
                        ${totalAmount.toFixed(2)}
                        {item.price?.recurring && (
                          <Text style={styles.subscriptionInterval}>
                            {` / ${item.price.recurring.interval}`}
                          </Text>
                        )}
                      </Text>
                    </View>
                  </View>
                </View>
              );
            })}
          </View>
        )}

        {/* Question responses if available */}
        {questionResponses && Object.keys(questionResponses).length > 0 && (
          <View style={styles.responsesContainer}>
            <View style={styles.subsectionHeader}>
              <MaterialIcons name="list-alt" size={18} color="#4A5568" />
              <Text style={styles.subsectionTitle}>Additional Information</Text>
            </View>
            {Object.entries(questionResponses).map(
              ([question, answer], index) => (
                <View key={index} style={styles.responseItem}>
                  <Text style={styles.questionText}>{question}</Text>
                  <Text style={styles.answerText}>{answer}</Text>
                </View>
              )
            )}
          </View>
        )}

        {/* Shipping address if available */}
        {hasShipping && (
          <View style={styles.shippingContainer}>
            <View style={styles.subsectionHeader}>
              <MaterialIcons name="local-shipping" size={18} color="#4A5568" />
              <Text style={styles.subsectionTitle}>Shipping Address</Text>
            </View>
            <View style={styles.addressContainer}>
              <Text style={styles.addressText}>
                {checkoutSession.shipping_details.name}
              </Text>
              {checkoutSession.shipping_details.address && (
                <>
                  <Text style={styles.addressText}>
                    {checkoutSession.shipping_details.address.line1}
                  </Text>
                  {checkoutSession.shipping_details.address.line2 && (
                    <Text style={styles.addressText}>
                      {checkoutSession.shipping_details.address.line2}
                    </Text>
                  )}
                  <Text style={styles.addressText}>
                    {checkoutSession.shipping_details.address.city},{" "}
                    {checkoutSession.shipping_details.address.state}{" "}
                    {checkoutSession.shipping_details.address.postal_code}
                  </Text>
                </>
              )}
            </View>
          </View>
        )}
      </View>
    );
  };

  // Render child section
  const renderChildSection = () => {
    if (!tcChildID) return null;

    if (isLoadingChild) {
      return (
        <View style={styles.childSection}>
          <View style={styles.sectionHeader}>
            <Icon icon="people" size={20} color="#4A5568" />
            <Text style={styles.sectionTitle}>Child Information</Text>
          </View>
          <View style={styles.childSkeletonContainer}>
            <Skeleton size="10" rounded="full" style={{ marginRight: 12 }} />
            <View style={{ flex: 1 }}>
              <Skeleton.Text lines={2} />
            </View>
          </View>
        </View>
      );
    }

    if (child) {
      return (
        <View style={styles.childSection}>
          <View style={styles.sectionHeader}>
            <Icon icon="people" size={20} color="#4A5568" />
            <Text style={styles.sectionTitle}>Child Information</Text>
          </View>
          <ChildCard child={child} classrooms={classrooms} />
        </View>
      );
    }

    return null;
  };

  // Get dynamic accent color
  const accentColor = Rex.getConfig()?.colors?.button || "#4299E1";

  return (
    <View style={styles.pageContent}>
      <NavBar navigation={navigation} text="Purchase Details" />
      <ScrollView style={styles.scrollView}>
        {/* Payment summary card */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <MaterialIcons name="payment" size={20} color="#4A5568" />
            <Text style={styles.sectionTitle}>Payment Summary</Text>
          </View>

          {/* Status badge */}
          <View style={styles.statusBadge}>
            <View
              style={[
                styles.statusIndicator,
                {
                  backgroundColor:
                    charge.status === "succeeded" ? "#2DD881" : "#F6AD55"
                }
              ]}
            />
            <Text style={styles.statusText}>
              {charge.status === "succeeded"
                ? "Paid"
                : charge.status.charAt(0).toUpperCase() +
                  charge.status.slice(1)}
            </Text>
          </View>

          {/* Amount highlight */}
          <View style={styles.amountContainer}>
            <Text style={styles.amountValue}>
              ${(charge.amount / 100).toFixed(2)}
            </Text>
            <Text style={styles.chargeDescription}>{productName}</Text>
          </View>

          {/* Details list */}
          <View style={styles.detailsList}>
            <View style={styles.detailRow}>
              <View style={styles.detailLabelContainer}>
                <MaterialIcons name="date-range" size={16} color="#718096" />
                <Text style={styles.label}>Date</Text>
              </View>
              <Text style={styles.value}>
                {moment(charge.created * 1000).format("MMM D, YYYY")}
              </Text>
            </View>

            <View style={styles.detailRow}>
              <View style={styles.detailLabelContainer}>
                <MaterialIcons name="credit-card" size={16} color="#718096" />
                <Text style={styles.label}>Payment Method</Text>
              </View>
              <Text style={styles.value}>{getPaymentMethodDisplay()}</Text>
            </View>
          </View>

          {charge.receipt_url && (
            <TouchableOpacity
              style={[styles.receiptButton, { borderColor: accentColor }]}
              onPress={() => Util.openURL(charge.receipt_url)}
            >
              <MaterialIcons name="receipt" size={16} color={accentColor} />
              <Text style={[styles.receiptButtonText, { color: accentColor }]}>
                View Receipt
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Purchase details section */}
        {renderPurchaseDetailsSection()}

        {/* Child information section */}
        {tcChildID && renderChildSection()}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  pageContent: {
    flex: 1,
    alignItems: "center",
    backgroundColor: "#F7FAFC"
  },
  scrollView: {
    flex: 1,
    padding: 16,
    width
  },
  section: {
    backgroundColor: "white",
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2D3748",
    marginLeft: 8
  },
  subsectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12
  },
  subsectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#4A5568",
    marginLeft: 6
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    backgroundColor: "#F7FAFC",
    borderRadius: 12,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginBottom: 16
  },
  statusIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6
  },
  statusText: {
    fontSize: 14,
    fontWeight: "500"
  },
  amountContainer: {
    alignItems: "center",
    marginBottom: 20,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF2F7"
  },
  amountValue: {
    fontSize: 36,
    fontWeight: "bold",
    color: "#2D3748",
    marginBottom: 4
  },
  chargeDescription: {
    fontSize: 16,
    color: "#718096"
  },
  detailsList: {
    marginBottom: 16
  },
  detailRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12
  },
  detailLabelContainer: {
    flexDirection: "row",
    alignItems: "center"
  },
  label: {
    color: "#718096",
    fontSize: 16,
    marginLeft: 6
  },
  value: {
    color: "#2D3748",
    fontSize: 16,
    fontWeight: "500"
  },
  receiptButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16
  },
  receiptButtonText: {
    fontSize: 14,
    fontWeight: "500",
    marginLeft: 6
  },
  lineItemsContainer: {
    marginTop: 4
  },
  lineItemContainer: {
    flexDirection: "row",
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF2F7"
  },
  lineItemInfo: {
    flex: 1
  },
  lineItemDetails: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 8
  },
  productImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
    marginRight: 16,
    backgroundColor: "#F7FAFC"
  },
  productName: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2D3748",
    marginBottom: 4
  },
  productDescription: {
    fontSize: 14,
    color: "#718096",
    marginBottom: 8,
    lineHeight: 20
  },
  productPrice: {
    fontSize: 16,
    fontWeight: "600",
    color: "#4A5568"
  },
  productQuantity: {
    fontSize: 14,
    color: "#718096",
    fontWeight: "500"
  },
  subscriptionInterval: {
    fontWeight: "400",
    color: "#718096"
  },
  totalContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#EDF2F7",
    marginTop: 4
  },
  totalLabel: {
    fontSize: 16,
    fontWeight: "600",
    color: "#4A5568"
  },
  totalAmount: {
    fontSize: 18,
    fontWeight: "700",
    color: "#2D3748"
  },
  shippingContainer: {
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#EDF2F7"
  },
  addressContainer: {
    backgroundColor: "#F7FAFC",
    padding: 12,
    borderRadius: 8
  },
  addressText: {
    fontSize: 14,
    color: "#4A5568",
    marginBottom: 2,
    lineHeight: 20
  },
  responsesContainer: {
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#EDF2F7"
  },
  responseItem: {
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF2F7"
  },
  questionText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#4A5568",
    marginBottom: 4
  },
  answerText: {
    fontSize: 14,
    color: "#718096",
    lineHeight: 20
  },
  childSection: {
    backgroundColor: "white",
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2
  },
  childSkeletonContainer: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12
  },
  loadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: 20
  },
  loadingText: {
    marginLeft: 8,
    color: "#718096",
    fontSize: 16
  }
});
